import { Logger } from '@west-australian-newspapers/typescript-log'
import {
    BreachWallContentType,
    SubscriptionPackageOptions,
} from '../user-registration/breach/helpers/BreachWallTypes'
import { fetchSubscriptionOffers } from '../user-registration/breach/services/subscription-offer-api'
import { CampaignContentType } from '../subscribe-with-google/rrme-campaigns/types'
import {
    EVERYDAY_DIGITAL_52_WEEKS_RATE_CODE,
    EVERYDAY_DIGITAL_INCREASE,
    WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
    WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL_INCREASE,
    WEEKLY_DIGITAL_RATE_CODE_INCREASE,
    WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
    WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE_INCREASE,
} from '../user-registration/breach/constants'

export type OfferGroupType = 'breach' | 'campaigns'

export const offerGroups: OfferGroupType[] = ['breach', 'campaigns']

export type OfferGroupContentType = BreachWallContentType | CampaignContentType

const rrmeCampaignPackages: Record<
    CampaignContentType,
    SubscriptionPackageOptions[]
> = {
    default: [
        {
            type: 'single-subscription',
            heading: 'Everyday Digital',
            price: '$8 per week',
            priceQualifier: 'Billed every four weeks.',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribe/new',
            cancellationMessage: 'Cancel anytime. Min cost $32.',
            googleSKU: 'SWGPD.2741-5035-6630-89115',
            weeklyPrice: EVERYDAY_DIGITAL_INCREASE,
            rateCode: 'WST_DIGI_$8',
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                '19 WA regional papers',
                'Daily puzzles',
            ],
        },
    ],
    'university-offer': [
        {
            type: 'single-subscription',
            heading: 'Everyday Digital',
            price: '$1 every 4 weeks for 12 months',
            originalPrice: '$32 every 4 weeks',
            priceQualifier: 'Billed every four weeks.',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribe/new',
            googleSKU: 'SWGPD.8208-8521-6835-11674',
            weeklyPrice: 0.25,
            rateCode: 'WST_DIGI_$8',
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                '19 WA regional papers',
                'Daily puzzles',
            ],
        },
    ],
    'default-annual-package': [
        {
            type: 'single-subscription',
            heading: 'Subscribe',
            price: '$8 per week',
            priceQualifier: 'Billed every four weeks.',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribenow/digital',
            cancellationMessage: 'Cancel anytime. Min cost $32.',
            googleSKU: 'SWGPD.1835-4609-9866-97776',
            weeklyPrice: EVERYDAY_DIGITAL_INCREASE,
            rateCode: WEEKLY_DIGITAL_RATE_CODE_INCREASE,
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                '19 WA regional papers',
                'Daily puzzles',
            ],
        },
        {
            type: 'single-subscription',
            heading: 'Subscribe',
            price: '$6 per week',
            priceQualifier: 'Billed annually.',
            ribbonTitle: 'SAVE 25%',
            ribbonOptions: 'bluePill',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribenow/annual-package',
            cancellationMessage: 'Cancel anytime. Min cost $312.',
            googleSKU: 'SWGPD.2349-0024-3173-99483',
            weeklyPrice: 6,
            rateCode: EVERYDAY_DIGITAL_52_WEEKS_RATE_CODE,
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Pay upfront and save',
                'Unlimited access to all articles',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
    ],
}

const rrmeBreachPackages: Record<
    BreachWallContentType,
    SubscriptionPackageOptions[]
> = {
    default: [
        {
            type: 'single-subscription',
            heading: 'Subscribe',
            price: '$8 per week',
            priceQualifier: 'Billed every four weeks.',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribe/new',
            cancellationMessage: 'Cancel anytime. Min cost $32.',
            googleSKU: 'SWGPD.1835-4609-9866-97776',
            weeklyPrice: EVERYDAY_DIGITAL_INCREASE,
            rateCode: WEEKLY_DIGITAL_RATE_CODE_INCREASE,
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                '19 WA regional papers',
                'Daily puzzles',
            ],
        },
        {
            type: 'single-subscription',
            heading: 'Everyday Digital + Weekend Papers',
            price: '$10 per week',
            priceQualifier: 'Billed every four weeks',
            packagePath: 'subscribenow/sat-sun+digital',
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            cancellationMessage: 'Cancel anytime. Min cost $40.',
            packageType: 'digitalprint',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Weekend papers delivered',
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
    ],
    'default-price-increase': [
        {
            type: 'single-subscription',
            heading: 'Subscribe',
            price: '$8 per week',
            priceQualifier: 'Billed every four weeks.',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribe/new',
            cancellationMessage: 'Cancel anytime. Min cost $32.',
            googleSKU: 'SWGPD.1835-4609-9866-97776',
            weeklyPrice: EVERYDAY_DIGITAL_INCREASE,
            rateCode: WEEKLY_DIGITAL_RATE_CODE_INCREASE,
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                '19 WA regional papers',
                'Daily puzzles',
            ],
        },
        {
            type: 'single-subscription',
            heading: 'Everyday Digital + Weekend Papers',
            price: '$10 per week',
            priceQualifier: 'Billed every four weeks',
            packagePath: 'subscribenow/sat-sun+digital',
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE_INCREASE,
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL_INCREASE,
            cancellationMessage: 'Cancel anytime. Min cost $40.',
            packageType: 'digitalprint',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Weekend papers delivered',
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
    ],
    'default-annual-package': [
        {
            type: 'single-subscription',
            heading: 'Subscribe',
            price: '$8 per week',
            priceQualifier: 'Billed every four weeks.',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribe/new',
            cancellationMessage: 'Cancel anytime. Min cost $32.',
            googleSKU: 'SWGPD.1835-4609-9866-97776',
            weeklyPrice: EVERYDAY_DIGITAL_INCREASE,
            rateCode: WEEKLY_DIGITAL_RATE_CODE_INCREASE,
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                '19 WA regional papers',
                'Daily puzzles',
            ],
        },
        {
            type: 'single-subscription',
            heading: 'Subscribe',
            price: '$6 per week',
            priceQualifier: 'Billed annually.',
            ribbonTitle: 'SAVE 25%',
            ribbonOptions: 'bluePill',
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            packagePath: 'subscribenow/annual-package',
            cancellationMessage: 'Cancel anytime. Min cost $312.',
            googleSKU: 'SWGPD.2349-0024-3173-99483',
            weeklyPrice: 6,
            rateCode: EVERYDAY_DIGITAL_52_WEEKS_RATE_CODE,
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Pay upfront and save',
                'Unlimited access to all articles',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
    ],
    'digital-radio-and-papers': [
        {
            type: 'radio-list-subscriptions',
            heading: 'Everyday Digital',
            prices: [
                {
                    price: '$6 per week',
                    priceQualifier: 'Billed annually',
                    packagePath: 'subscribenow/annual-package',
                    rateCode: EVERYDAY_DIGITAL_52_WEEKS_RATE_CODE,
                    weeklyPrice: 6,
                    googleSKU: 'SWGPD.2349-0024-3173-99483',
                    ribbonTitle: 'SAVE 25%',
                    ribbonOptions: 'bluePill',
                    cancellationMessage: 'Cancel anytime. Min cost $312.',
                },
                {
                    price: '$8 per week',
                    priceQualifier: 'Billed every four weeks',
                    packagePath: 'subscribe/new',
                    rateCode: WEEKLY_DIGITAL_RATE_CODE_INCREASE,
                    weeklyPrice: EVERYDAY_DIGITAL_INCREASE,
                    googleSKU: 'SWGPD.1835-4609-9866-97776',
                    cancellationMessage: 'Cancel anytime. Min cost $32.',
                },
            ],
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
        {
            type: 'radio-list-subscriptions',
            heading: 'Everyday Digital + Weekend Papers',
            prices: [
                {
                    price: '$10 per week',
                    priceQualifier: 'Billed every four weeks',
                    packagePath: 'subscribenow/sat-sun+digital',
                    rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE_INCREASE,
                    weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL_INCREASE,
                    cancellationMessage: 'Cancel anytime. Min cost $40.',
                },
            ],
            packageType: 'digitalprint',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-blue',
            benefitOverride: [
                'Weekend papers delivered',
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
    ],
    sport: [],
    'black-friday': [
        {
            type: 'single-subscription',
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BLKFRI1',
            heading: 'Everyday Digital',
            price: '$4/week',
            originalPrice: '$8 per week',
            billingCycle: 'Billed every week',
            priceQualifier: '*for 8 weeks',
            packageType: 'digital',
            ribbonTitle: 'On sale',
            ribbonOptions: 'cornerDesignTag',
            buttonStyle: 'solid-blue',
            offerCode: 'BLKFRI1',
            googleSKU: 'everyday_digital_50_off',
            cancellationMessage: 'Cancel anytime. Min cost $32.00',
            weeklyPrice: 8,
            promoPrice: 4,
            discount: 4,
            rateCode: 'WST_DIGI_4X8_1',
            benefitOverride: [
                'Unlimited access to thewest.com.au',
                'Digital version of Today’s Paper',
                'Daily puzzles',
                '19 WA regional titles',
            ],
        },
        {
            type: 'single-subscription',
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BLKFRI2',
            heading: 'Everyday Digital + Weekend Papers',
            price: '$5/week',
            originalPrice: '$10 per week',
            billingCycle: 'Billed every four weeks',
            priceQualifier: '*for 8 weeks',
            packageType: 'digitalprint',
            ribbonTitle: 'On sale',
            ribbonOptions: 'cornerDesignTag',
            buttonStyle: 'solid-blue',
            offerCode: 'BLKFRI2',
            cancellationMessage: 'Cancel anytime. Min cost $40.00',
            weeklyPrice: 10,
            promoPrice: 5,
            discount: 5,
            rateCode: 'WST_PrSSDIG_5x8_1',
        },
    ],
    'vanishing-cousins': [],
    'top-50-schools': [],
    '50-off-afl-breach-screen': [],
    '50-off-breach-discount': [],
    'subs-campaign-eofy-weekend-digital': [],
    'business-pack': [],
    'sport-pack': [],
    'digital-only': [],
    Puzzles: [],
    'Puzzles-increase': [],
    'puzzles-swg-30-off': [],
    ePaper: [],
    'ePaper-price-increase': [],
    'default-breachwall-2024': [],
    'summer-of-cricket': [],
    'afl-footy-lives-here-breach-screen': [
        {
            type: 'radio-list-subscriptions',
            heading: '12 weeks',
            prices: [
                {
                    packagePath:
                        'offercheck/thewest?redirect=true&offerCode=DIGFT3M',
                    offerCode: 'DIGFT3M',
                    price: '$7.20/week',
                    weeklyPrice: 8,
                    promoPrice: 7.2,
                    discount: 0.8,
                    quantity: 12,
                    packageTitle: 'Digital',
                    packageType: 'digital',
                    rateCode: 'WST_DIGI_3MTH10OFF',
                    cancellationMessage: 'Cancel anytime. Min cost $86.40.',
                },
                {
                    packagePath:
                        'offercheck/thewest?redirect=true&offerCode=SSFT3M',
                    offerCode: 'SSFT3M',
                    price: '$9.00/week',
                    weeklyPrice: 10,
                    promoPrice: 9,
                    discount: 1,
                    quantity: 12,
                    packageTitle: 'Digital + Weekend papers',
                    packageType: 'digitalprint',
                    rateCode: 'WST_PrSSDIG_FT_3M',
                    cancellationMessage: 'Cancel anytime. Min cost $108',
                    additionalBenefits: ['Weekend papers delivered.'],
                },
            ],
            columnPosition: {
                mobile: '2',
                desktop: '1',
            },
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-yellow',
            benefitOverride: [
                'Access to expert AFL and WAFL analysis.',
                'Exclusive footy offers and competitions.',
                'Daily digital newspaper edition, footy lift-outs, and magazines.',
                'Unlimited access to thewest.com.au and the West app.',
            ],
            ribbonTitle: 'Save 10%',
            ribbonOptions: 'bluePillLight',
        },
        {
            type: 'radio-list-subscriptions',
            heading: '36 weeks',
            prices: [
                {
                    packagePath:
                        'offercheck/thewest?redirect=true&offerCode=DIGFT9M',
                    offerCode: 'DIGFT9M',
                    price: '$6.80/week',
                    weeklyPrice: 8,
                    promoPrice: 6.8,
                    discount: 1.2,
                    quantity: 36,
                    packageTitle: 'Digital',
                    packageType: 'digital',
                    rateCode: 'WST_DIGI_9MTH15OFF',
                    cancellationMessage: 'Cancel anytime. Min cost $244.80',
                },
                {
                    packagePath:
                        'offercheck/thewest?redirect=true&offerCode=SSFT9M',
                    offerCode: 'SSFT9M',
                    price: '$8.50/week',
                    weeklyPrice: 10,
                    promoPrice: 8.5,
                    discount: 1.5,
                    quantity: 36,
                    packageTitle: 'Digital + Weekend papers',
                    packageType: 'digitalprint',
                    rateCode: 'WST_PrSSDIG_FT_9M',
                    cancellationMessage: 'Cancel anytime. Min cost $306',
                    additionalBenefits: ['Weekend papers delivered.'],
                },
            ],
            columnPosition: {
                mobile: '3',
                desktop: '3',
            },
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-yellow',
            benefitOverride: [
                'Access to expert AFL and WAFL analysis.',
                'Exclusive footy offers and competitions.',
                'Daily digital newspaper edition, footy lift-outs, and magazines.',
                'Unlimited access to thewest.com.au and the West app.',
            ],
            ribbonTitle: 'Save 15%',
            ribbonOptions: 'bluePillLight',
        },
        {
            type: 'radio-list-subscriptions',
            heading: '52 weeks',
            prices: [
                {
                    packagePath:
                        'offercheck/thewest?redirect=true&offerCode=DIGFT12M',
                    offerCode: 'DIGFT12M',
                    price: '$6.00/week',
                    weeklyPrice: 8,
                    promoPrice: 6,
                    discount: 2,
                    quantity: 52,
                    packageTitle: 'Digital',
                    packageType: 'digital',
                    rateCode: 'WST_DIG_12MTH25FT',
                    cancellationMessage: 'Cancel anytime. Min cost $312',
                },
                {
                    packagePath:
                        'offercheck/thewest?redirect=true&offerCode=SSFT12M',
                    offerCode: 'SSFT12M',
                    price: '$7.50/week',
                    weeklyPrice: 10,
                    promoPrice: 7.5,
                    discount: 2.5,
                    quantity: 52,
                    packageTitle: 'Digital + Weekend papers',
                    packageType: 'digitalprint',
                    rateCode: 'WST_PrSSDIG_FT_12M',
                    cancellationMessage: 'Cancel anytime. Min cost $390',
                    additionalBenefits: ['Weekend papers delivered.'],
                },
            ],
            columnPosition: {
                mobile: '1',
                desktop: '2',
            },
            isRecommended: true,
            packageType: 'digital',
            packageTheme: 'optimise-blue',
            buttonStyle: 'solid-yellow',
            benefitOverride: [
                'Access to expert AFL and WAFL analysis.',
                'Exclusive footy offers and competitions.',
                'Daily digital newspaper edition, footy lift-outs, and magazines.',
                'Unlimited access to thewest.com.au and the West app.',
            ],
            ribbonTitle: 'Save 25%',
            ribbonOptions: 'bluePillLight',
        },
    ],
}

const rrmeSubscriptionPackages: Record<
    OfferGroupType,
    Record<string, SubscriptionPackageOptions[]>
> = {
    campaigns: rrmeCampaignPackages,
    breach: rrmeBreachPackages,
}

let cachedRrmeSubscriptionPackages: Record<
    string,
    Record<string, SubscriptionPackageOptions[]>
> = {}

export const getRrmeOfferPackages = (
    offerGroup: OfferGroupType,
    log: Logger,
): Record<string, SubscriptionPackageOptions[]> => {
    // check if the cachedRrmeSubscriptionPackages is undefined or null
    if (
        !cachedRrmeSubscriptionPackages ||
        Object.keys(cachedRrmeSubscriptionPackages).length === 0
    ) {
        cachedRrmeSubscriptionPackages = tryAndGetOffersFromLocalStorage({
            log,
        })
    }

    return (
        cachedRrmeSubscriptionPackages[offerGroup] ??
        rrmeSubscriptionPackages[offerGroup]
    )
}

/**
 * Loads subscription offers from the subscription offer API and caches them.
 */
export const loadRrmeOfferGroupFromApi = async (
    publicationId: string,
    log: Logger,
    offerGroup: OfferGroupType,
    isSSR: boolean,
    variant?: string,
    config?: { subscriptionOfferApi?: string },
): Promise<Record<string, Record<string, SubscriptionPackageOptions[]>>> => {
    try {
        const offers = await fetchSubscriptionOffers(
            publicationId,
            log,
            offerGroup,
            variant,
            config,
        )

        cachedRrmeSubscriptionPackages[offerGroup] = offers

        // Don't try store to localStorage on the server, as it doesn't exist
        if (!isSSR) {
            storeOffersToLocalStorage(cachedRrmeSubscriptionPackages, log)
        }

        log.info(
            { publicationId, cachedRrmeSubscriptionPackages, variant },
            'Successfully loaded subscription offers from API',
        )

        return cachedRrmeSubscriptionPackages
    } catch (error) {
        log.error(
            { error, publicationId, variant },
            'Failed to load subscription offers from API, attempting to load from localStorage or falling back to static data',
        )

        cachedRrmeSubscriptionPackages = tryAndGetOffersFromLocalStorage({
            log,
            publicationId,
            variant,
        })

        return cachedRrmeSubscriptionPackages
    }
}

function storeOffersToLocalStorage(
    offers: Record<string, Record<string, SubscriptionPackageOptions[]>>,
    log: Logger,
) {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(
                'cachedRrmeSubscriptionPackages',
                JSON.stringify(offers),
            )
        }
    } catch (e) {
        log.warn(
            { error: e },
            'Failed to store subscription offers to localStorage',
        )
    }
}

function tryAndGetOffersFromLocalStorage({
    log,
    publicationId,
    variant,
}: {
    log: Logger
    publicationId?: string
    variant?: string
}): Record<string, Record<string, SubscriptionPackageOptions[]>> {
    try {
        if (window.localStorage) {
            const localStorageCachedBreachPackages =
                window.localStorage.getItem('cachedRrmeSubscriptionPackages')

            if (localStorageCachedBreachPackages) {
                try {
                    const parsedOffers = JSON.parse(
                        localStorageCachedBreachPackages,
                    ) as Record<
                        string,
                        Record<string, SubscriptionPackageOptions[]>
                    >

                    log?.info(
                        { publicationId, variant },
                        'Loaded subscription offers from localStorage',
                    )
                    return parsedOffers
                } catch (e) {
                    log?.error(
                        { error: e, publicationId, variant },
                        'Failed to parse subscription offers from localStorage, falling back to static data',
                    )
                }
            }
        }
    } catch (e) {
        log?.error(
            { error: e, publicationId, variant },
            'Failed to access localStorage, falling back to static data',
        )
    }
    return rrmeSubscriptionPackages
}
